import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import useToggle from 'hooks/useToggle'

type Props = {
  heading: string
  subHeading?: string
  showBorder?: boolean
  image?: App.Image
  imgUrl?: string
  onSelect?: (checked: boolean) => void
}

const Root = styled(Clickable)`
  display: flex;
  gap: ${rem(12)};
  border-radius: ${(props) => props.theme.borderRadius.S};
  align-items: center;
  padding: ${rem(20)};
  width: 100%;

  &.hide-border {
    padding: 0;
  }

  &.package-container {
    display: flex;
    align-items: flex-start;
    flex-direction: row;
    gap: ${rem(12)};

    .package-items {
      flex-direction: column;
    }
  }

  > .click-disabled {
    pointer-events: none;
  }
`

const Img = styled.img`
  max-width: 100%;
  max-height: 100%;
`

const ImgContainer = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  display: flex;
  justify-content: center;
  flex-direction: column;
  flex-shrink: 0;
`

const BookingItemImage = styled(ResponsiveImage)`
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const ImagePlaceHolder = styled.div`
  min-height: ${rem(48)};
  min-width: ${rem(48)};
  background: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

function BookingItemCard(props: Props) {
  const {
    heading,
    subHeading,
    image,
    showBorder = true,
    imgUrl,
    onSelect,
  } = props
  const intl = useIntl()
  const { value, toggle } = useToggle(false)
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const onClick = useCallback(() => {
    toggle()
    onSelect?.(!value)
  }, [onSelect, toggle, value])

  return (
    <Root className={clsx({ 'hide-border': !showBorder })} onClick={onClick}>
      {showBorder && (
        <CircleCheckboxInput
          className="click-disabled"
          ref={checkBoxRef}
          checked={value}
        />
      )}
      {image?.id && (
        <BookingItemImage
          aria-hidden="true"
          alt={intl.formatMessage({
            id: 'JEfoOEzZTngI3YbN91O7',
            defaultMessage: 'Package preview',
            description: 'Accessibility alt text for booking item package preview image',
          })}
          id={image.id}
          mobileAspectRatio="1:1"
          width={48}
        />
      )}
      {!image && imgUrl && (
        <ImgContainer>
          <Img alt={heading} src={imgUrl} />
        </ImgContainer>
      )}
      {!image && !imgUrl && <ImagePlaceHolder />}
      <div>
        {!showBorder && (
          <BodyText lineClamp={1} variant="large" colour="neutral-one">
            {heading}
          </BodyText>
        )}
        {showBorder && (
          <Heading lineClamp={1} variant="heading6">
            {heading}
          </Heading>
        )}
        {subHeading && (
          <BodyText variant="large" colour="neutral-two">
            {subHeading}
          </BodyText>
        )}
      </div>
    </Root>
  )
}

export default BookingItemCard
